// CoreUI Pro source references: /Users/guochunzhong/git/oss/coreui-pro/js/src/date-range-picker.js, /Users/guochunzhong/git/oss/coreui-pro/js/src/time-picker.js
import { createPopper } from "@popperjs/core"
import { parseDate, validLocalDate } from "./dom"

export function delayedInput(component, input, callback) {
  let timer
  component.listen(input, "input", () => {
    clearTimeout(timer)
    timer = setTimeout(callback, Number(component.config.inputOnChangeDelay ?? 750))
  })
  component.listen(input, "change", () => {
    clearTimeout(timer)
    callback()
  })
  component.signal.addEventListener("abort", () => clearTimeout(timer), { once: true })
}

export function pickerLabel(element) {
  return (
    element.dataset.demoPickerLabel ||
    document.getElementById(element.getAttribute("aria-labelledby"))?.textContent.trim()
  )
}

export function button(component, label, className, action) {
  const element = document.createElement("button")
  element.type = "button"
  element.className = className
  element.textContent = label
  component.listen(element, "click", action)
  return element
}

export function twoDigits(value) {
  return String(value).padStart(2, "0")
}

export function timeFormat(config) {
  return {
    hour: "numeric",
    minute: config.minutes ? "2-digit" : undefined,
    second: config.minutes && config.seconds ? "2-digit" : undefined,
  }
}

export function parsePickerDate(value, config) {
  if (config.inputDateParse) return config.inputDateParse(value)
  if (config.selectionType && config.selectionType !== "day") return parseDate(value, config.selectionType)
  const text = String(value).trim()
  // Accept explicit year-first dates as well as the numeric format displayed by this picker.
  if (/^\d{4}[/-]\d{1,2}[/-]\d{1,2}(?:[ T]\d{1,2}:\d{2}(?::\d{2})?\s*(?:AM|PM)?)?$/i.test(text))
    return parseDate(text)
  const fields = []
  const parts = new Intl.DateTimeFormat(config.locale, {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    ...(config.timepicker ? timeFormat(config) : {}),
  }).formatToParts(new Date(2026, 10, 23, 17, 34, 56))
  const pattern = parts
    .map(({ type, value }) => {
      if (type === "literal") return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&").replace(/\s+/g, "\\s*")
      fields.push(type)
      return type === "dayPeriod" ? "(.+?)" : "(\\d{1,4})"
    })
    .join("")
  const match = text.match(new RegExp(`^${pattern}$`, "i"))
  if (!match) return null
  const values = Object.fromEntries(fields.map((field, index) => [field, match[index + 1]]))
  let hour = Number(values.hour || 0)
  if (values.dayPeriod) {
    if (hour < 1 || hour > 12) return null
    const pm = new Intl.DateTimeFormat(config.locale, { hour: "numeric", hour12: true })
      .formatToParts(new Date(2026, 0, 1, 17))
      .find((part) => part.type === "dayPeriod").value
    hour = (hour % 12) + (values.dayPeriod.toLowerCase() === pm.toLowerCase() ? 12 : 0)
  }
  return validLocalDate(
    Number(values.year),
    Number(values.month) - 1,
    Number(values.day),
    hour,
    Number(values.minute || 0),
    Number(values.second || 0),
  )
}

export function attachMenu(component) {
  const container = component.config.container
  const parent =
    container === true
      ? document.body
      : container instanceof Element
        ? container
        : typeof container === "string"
          ? document.querySelector(container)
          : null
  if (parent && component.menu) {
    parent.append(component.menu)
    component.generated.push(component.menu)
    component.detachedMenu = true
  }
}

export function showMenu(component) {
  component.previouslyFocused = document.activeElement
  component.menu?.classList.add("show")
  if (component.menu)
    component.popper = createPopper(component.toggler, component.menu, {
      placement: "bottom-start",
      modifiers: [{ name: "preventOverflow", options: { padding: 8 } }],
    })
}

export function pickerTab(component, event) {
  if (event.key !== "Tab" || !component.detachedMenu || !component.element.classList.contains("show")) return
  const focusable = (root) =>
    [
      ...root.querySelectorAll('button:not(:disabled), input:not(:disabled), select:not(:disabled), [tabindex="0"]'),
    ].filter((element) => element.tabIndex >= 0 && element.getClientRects().length)
  const host = focusable(component.element)
  const menu = focusable(component.menu)
  if (!host.length || !menu.length) return
  const target = event.shiftKey
    ? event.target === host[0]
      ? menu.at(-1)
      : event.target === menu[0]
        ? host.at(-1)
        : null
    : event.target === host.at(-1)
      ? menu[0]
      : event.target === menu.at(-1)
        ? host[0]
        : null
  if (target) {
    event.preventDefault()
    target.focus({ preventScroll: true })
  }
}

export function hideMenu(component) {
  const heldFocus = component.menu?.contains(document.activeElement)
  component.menu?.classList.remove("show")
  component.popper?.destroy()
  component.popper = null
  if (heldFocus)
    (component.previouslyFocused?.isConnected
      ? component.previouslyFocused
      : component.input || component.startInput
    )?.focus({ preventScroll: true })
}
